import _ from 'lodash';
import type { Socket } from 'phoenix';
import {
  SESSION_ORGANIZATION,
  SESSION_START,
  SESSION_USER,
  SESSION_USER_SETTING,
} from '../constants/redux';
import type { Organization, User } from '../graphql/generated/graphqlTypes';
import {
  AuthenticationMode,
  BillingSummaryStatus,
  PlanTier,
  UserType,
} from '../graphql/generated/graphqlTypes';

const initialOrganization = {
  id: '',
  email: '',
  host: '',
  effectiveHost: '',
  name: '',
  logo: { thumb: null },
  planTier: PlanTier.Startup,
  periodEndAt: null,
  trialEndAt: null,
  isTrialEnded: false,
  isTrialEndingSoon: false,
  isLocked: false,
  billingStatus: BillingSummaryStatus.Active,
  settings: {
    authentication: AuthenticationMode.SsoAndPassword,
    seatLimit: null,
  },
  isVerified: false,
};

const initialUser = {
  id: '',
  name: '',
  avatar: {},
  type: UserType.User,
  email: '',
  settings: {
    notifications: {
      direct: { email: false, slack: false },
      participant: { email: false, slack: false },
      team: { email: false, slack: false },
    },
  },
  membershipGroups: [],
  identities: [],
  description: null,
};

export interface SessionState {
  authToken: string;
  intercomHash: string;
  organization: Pick<Organization, keyof typeof initialOrganization>;
  user: Pick<User, keyof typeof initialUser>;
  socket?: Socket;
}

const initialState: SessionState = {
  authToken: '',
  intercomHash: '',
  organization: initialOrganization,
  user: initialUser,
  socket: undefined,
};

// TODO: export from namespaces fall within that rule, remove once namespace is changed
/* eslint-disable import/no-unused-modules */

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Actions {
  export interface SESSION_START {
    type: typeof SESSION_START;
    authToken: string;
    intercomHash: string;
    socket: Socket;
  }

  export interface SESSION_ORGANIZATION {
    type: typeof SESSION_ORGANIZATION;
    organization: Partial<Organization>;
  }

  export interface SESSION_USER {
    type: typeof SESSION_USER;
    user: Partial<User>;
  }

  export interface SESSION_USER_SETTING {
    type: typeof SESSION_USER_SETTING;
    setting: string;
    value: unknown;
  }

  export interface OTHER {
    type?: undefined;
  }

  export type ANY =
    | SESSION_START
    | SESSION_ORGANIZATION
    | SESSION_USER
    | SESSION_USER_SETTING
    | OTHER;
}

export default function sessionReducer(
  state = initialState,
  action: Actions.ANY = {},
) {
  switch (action.type) {
    case SESSION_START:
      return {
        ...state,
        authToken: action.authToken,
        intercomHash: action.intercomHash,
        socket: action.socket,
      };

    case SESSION_ORGANIZATION: {
      return {
        ...state,
        organization: {
          ...state.organization,
          ...action.organization,
        },
      };
    }

    case SESSION_USER:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.user,
        },
      };

    case SESSION_USER_SETTING:
      return {
        ...state,
        user: {
          ...state.user,
          settings: _.merge({}, state.user.settings, action.value),
        },
      };

    default:
      return state;
  }
}
